import React from 'react';
import { Calendar, Clock, CalendarDays, CalendarRange } from 'lucide-react';
import { TimePeriod, PERIOD_DESCRIPTIONS } from '../../types';

interface PeriodSelectorProps {
  selectedPeriod: TimePeriod;
  onPeriodChange: (period: TimePeriod) => void;
  darkMode: boolean;
  showLabel?: boolean;
}

const SHORT_LABELS: Record<TimePeriod, string> = {
  day: 'Jour',
  week: 'Sem.',
  month: 'Mois',
  year: 'Année'
};

const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  selectedPeriod,
  onPeriodChange,
  darkMode,
  showLabel = true
}) => {
  const periods: Array<{ value: TimePeriod; icon: typeof Clock }> = [
    { value: 'day', icon: Clock },
    { value: 'week', icon: Calendar },
    { value: 'month', icon: CalendarDays },
    { value: 'year', icon: CalendarRange }
  ];

  return (
    <div className="flex flex-col space-y-2 md:space-y-3">
      {showLabel && (
        <label className={`text-xs md:text-sm font-medium ${
          darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65'
        }`}>
          Période d'affichage
        </label>
      )}

      {/* Mobile: compact segmented control */}
      <div
        className={`md:hidden inline-flex w-full rounded-lg p-1 ${
          darkMode ? 'bg-appines-white/10' : 'bg-[rgba(1,42,54,0.06)]'
        }`}
        role="tablist"
        aria-label="Période d'affichage"
      >
        {periods.map(({ value, icon: Icon }) => {
          const isSelected = selectedPeriod === value;
          return (
            <button
              key={value}
              role="tab"
              aria-selected={isSelected}
              onClick={() => onPeriodChange(value)}
              className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                isSelected
                  ? 'bg-appines-petrol text-appines-white shadow-sm'
                  : darkMode
                    ? 'text-appines-white/65 hover:text-appines-white'
                    : 'text-appines-petrol/65 hover:text-appines-petrol'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              <span>{SHORT_LABELS[value]}</span>
            </button>
          );
        })}
      </div>

      {/* Desktop: rich 4-column grid */}
      <div className="hidden md:grid grid-cols-4 gap-3">
        {periods.map(({ value, icon: Icon }) => {
          const isSelected = selectedPeriod === value;
          return (
            <button
              key={value}
              onClick={() => onPeriodChange(value)}
              className={`
                relative group flex flex-col items-center justify-center
                px-4 py-3 rounded-xl font-medium text-sm
                transition-all duration-200 transform
                ${isSelected
                  ? 'bg-appines-petrol text-appines-white shadow-lg scale-105 ring-2 ring-appines-emerald ring-offset-2'
                  : darkMode
                    ? 'bg-appines-white/10 text-appines-white/65 hover:bg-appines-white/15 hover:scale-102'
                    : 'bg-[rgba(1,42,54,0.06)] text-appines-petrol/65 hover:bg-[rgba(1,42,54,0.10)] hover:scale-102'
                }
                ${darkMode && isSelected ? 'ring-offset-appines-petrol' : 'ring-offset-appines-white'}
              `}
            >
              <Icon className="h-5 w-5 mb-1" />

              <span className={`text-xs mt-0.5 ${
                isSelected ? 'text-appines-white/80' : darkMode ? 'text-appines-white/45' : 'text-appines-petrol/45'
              }`}>
                {PERIOD_DESCRIPTIONS[value]}
              </span>

              {isSelected && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-appines-emerald rounded-full border-2 border-appines-white" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PeriodSelector;
